// Personal settings - the "me" half of the settings split (org settings live
// under Administration/Organization for admins). Password, sign-in methods,
// and per-user preferences.
import React, { useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Divider, Stack, Switch, TextField, Typography,
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const authClient = axios.create({ baseURL: '/auth' });
authClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function SettingsPage() {
  const { user } = useAuth();
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [msg, setMsg] = useState(null);
  const [emotional, setEmotional] = useState(
    localStorage.getItem('pref-emotional-insights') === '1');

  const changePassword = async () => {
    setMsg(null);
    if (pw.next !== pw.confirm) { setMsg({ sev: 'error', text: 'New passwords do not match.' }); return; }
    if (pw.next.length < 8) { setMsg({ sev: 'error', text: 'New password must be at least 8 characters.' }); return; }
    try {
      await authClient.post('/change-password', { current_password: pw.current, new_password: pw.next });
      setMsg({ sev: 'success', text: 'Password changed.' });
      setPw({ current: '', next: '', confirm: '' });
    } catch (e) {
      setMsg({ sev: 'error', text: e.response?.data?.detail || 'Could not change password.' });
    }
  };

  const toggleEmotional = (on) => {
    setEmotional(on);
    localStorage.setItem('pref-emotional-insights', on ? '1' : '0');
  };

  return (
    <Box sx={{ p: 3, maxWidth: 640 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>Settings</Typography>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography fontWeight={700} sx={{ mb: 1 }}>Sign-in methods</Typography>
          <Typography variant="body2" color="text.secondary">
            Signed in as <b>{user?.email}</b>. Email + password, Google and Microsoft
            sign-in are all accepted for this account (matched by email).
          </Typography>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography fontWeight={700} sx={{ mb: 2 }}>Change password</Typography>
          <Stack spacing={2}>
            <TextField type="password" size="small" label="Current password"
              value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} />
            <TextField type="password" size="small" label="New password"
              value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} />
            <TextField type="password" size="small" label="Confirm new password"
              value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
            {msg && <Alert severity={msg.sev}>{msg.text}</Alert>}
            <Box><Button variant="contained" onClick={changePassword}
              disabled={!pw.current || !pw.next}>Update password</Button></Box>
            <Typography variant="caption" color="text.secondary">
              Signed up via Google/Microsoft only? Password sign-in activates after an
              admin sets an initial password for your account.
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography fontWeight={700} sx={{ mb: 1 }}>Preferences</Typography>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography>Emotional insights</Typography>
              <Typography variant="caption" color="text.secondary">
                Off by default: reports lead with fact-based indices. Turning this on
                shows the emotional-arc layers where available (this device only).
              </Typography>
            </Box>
            <Switch checked={emotional} onChange={(e) => toggleEmotional(e.target.checked)} />
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            Organization-wide settings (industry pack, report branding, SSO, users) live
            under Administration and are managed by your org admin.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
